import React from 'react';
import { SxStyle } from '../../../../types';
import { FIELD_TYPES, ROLES, TeamRoles } from '../../../../types';
import Accordion from '@mui/material/Accordion/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UserTeamsRoles from './user-teams-roles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MultipleSelectField from '../../../common/fields/multiple-select-field';
import { useStringsContext } from '../../../../contexts/strings';
import { PermissionsTeam, TeamMinimized } from '../types';

const styles: SxStyle = {
  accordion: {
    marginBottom: '16px',
  },
  summaryText: {
    flexShrink: 0,
    marginRight: '5px',
    '&::first-letter': {
      textTransform: 'uppercase',
    },
  },
  roleContainer: {
    marginBottom: '20px',
  },
  roleText: {
    textTransform: 'capitalize',
    marginBottom: '12px',
  },
};

interface Props {
  title: string;
  permissionsTeam?: PermissionsTeam;
  teams: TeamMinimized[];
  onChange: (permissionsTeam: PermissionsTeam) => void;
}

const EditRoleAccordion = ({ title, permissionsTeam, teams, onChange }: Props) => {
  const { strings } = useStringsContext();

  const filteredRoles = Object.values(ROLES).filter((role) => role !== ROLES.NONE);

  const handleChange = (teamRole: TeamRoles, teamAbbrvs: string[]) => {
    const newPermissionsTeam = {
      ...permissionsTeam,
      [teamRole]: teamAbbrvs,
    } as PermissionsTeam;

    onChange(newPermissionsTeam);
  };

  return !teams ? (
    <></>
  ) : (
    <Accordion sx={styles.accordion} elevation={3} disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        style={{ justifyContent: 'flex-start', overflow: 'hidden' }}
      >
        <Box sx={styles.summaryText}>
          <Typography variant="body2-bold">{title}</Typography>
        </Box>
        {permissionsTeam && (
          <UserTeamsRoles appTeams={teams} permissionsTeam={permissionsTeam} minimized />
        )}
      </AccordionSummary>
      <AccordionDetails>
        {teams &&
          filteredRoles.map((role) => (
            <Box sx={styles.roleContainer} key={role}>
              <Typography variant="body1-bold" sx={styles.roleText}>
                {role}
              </Typography>
              <MultipleSelectField
                setChange={(key, value) => handleChange(role, value)}
                setValidation={null}
                field={{
                  title: strings.usersAndPermissions.selectTeam,
                  id: 'permissions',
                  type: FIELD_TYPES.MULTI_SELECT,
                  key: 'permissions',
                  value: (permissionsTeam && permissionsTeam[role]) || [],
                  options: teams.map((team) => team.abbrv),
                  optionsText: teams.map((team) => team.name),
                  optionsDisabled: filteredRoles
                    .filter((otherRole) => otherRole !== role)
                    .reduce((teamAbbrvs, otherRole) => {
                      return [
                        ...teamAbbrvs,
                        ...((permissionsTeam && permissionsTeam[otherRole]) || []),
                      ];
                    }, [] as string[]),
                }}
                isReadOnly={false}
                fullWidth={true}
              />
            </Box>
          ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default EditRoleAccordion;
